import { yupResolver } from '@hookform/resolvers/yup'
import { ELoanPurpose } from '@if/core/types'
import { ScrollableAlert } from '@src/components'
import { MouseEventHandler, useCallback, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { LoanPurposeInput, LoanPurposeInputSchema } from './PrequalificationSchema'
import { FORM_ID } from './StepperFormFooter'

interface Props {
  onPrequalificationUpdated: (data: LoanPurposeInput) => void
  prequalificationData: LoanPurposeInput
}

interface LoanPurposeTileProps {
  id: ELoanPurpose | 'homeImprovement' | 'other'
  icon: string
  checked: boolean
  onClick: MouseEventHandler
}

const LoanPurposeTile = ({ id, icon, checked, onClick }: LoanPurposeTileProps) => {
  const { t } = useTranslation()
  return (
    <div className="radio-wrap">
      <input type="radio" value={id} id={id} checked={checked} name="loanPurpose" onClick={onClick} readOnly />
      <label htmlFor={id}>
        <i className={`fa-light fa-${icon}`} />
        <span>{t(`enum.eLoanPurpose.${id}`)}</span>
      </label>
    </div>
  )
}

const LoanPurposeForm = ({ prequalificationData, onPrequalificationUpdated }: Props) => {
  const { t } = useTranslation()

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<LoanPurposeInput>({
    mode: 'onBlur',
    resolver: yupResolver(LoanPurposeInputSchema),
    defaultValues: prequalificationData,
  })

  const bottomRef = useRef<HTMLDivElement | null>(null)

  const selectedLoanPurposeId = watch('loanPurposeId')
  const handleLoanPurposeSelected = useCallback<MouseEventHandler<HTMLInputElement>>(
    (event) => {
      setValue('loanPurposeId', event.currentTarget.value as ELoanPurpose)
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    },
    [setValue, bottomRef],
  )

  return (
    <section className="step-content">
      <h3 className="form-question">{t('loanPurpose.purpose')}</h3>

      <div className="paragraph">
        <p>{t('loanPurpose.sharePurpose')}</p>
      </div>

      <form onSubmit={handleSubmit(onPrequalificationUpdated)} id={FORM_ID}>
        <ScrollableAlert showAlert={!!errors.loanPurposeId} type="error" message={t('common.loanPurposeSelectError')} />
        <div className="control-group">
          <div className="radiobuttons-wrap with-icon">
            <LoanPurposeTile
              id={ELoanPurpose.Medical}
              icon="hospital"
              checked={selectedLoanPurposeId === ELoanPurpose.Medical}
              onClick={handleLoanPurposeSelected}
            />

            <LoanPurposeTile
              id={ELoanPurpose.Dentistry}
              icon="tooth"
              checked={selectedLoanPurposeId === ELoanPurpose.Dentistry}
              onClick={handleLoanPurposeSelected}
            />

            <LoanPurposeTile
              id={ELoanPurpose.Veterinary}
              icon="dog"
              checked={selectedLoanPurposeId === ELoanPurpose.Veterinary}
              onClick={handleLoanPurposeSelected}
            />

            <LoanPurposeTile
              id={'homeImprovement'}
              icon="house"
              checked={selectedLoanPurposeId === 'homeImprovement'}
              onClick={handleLoanPurposeSelected}
            />

            <LoanPurposeTile
              id={ELoanPurpose.GoodsAndServices}
              icon="loveseat"
              checked={selectedLoanPurposeId === ELoanPurpose.GoodsAndServices}
              onClick={handleLoanPurposeSelected}
            />

            <LoanPurposeTile
              id={'other'}
              icon="envelope-open-dollar"
              checked={selectedLoanPurposeId === 'other'}
              onClick={handleLoanPurposeSelected}
            />
          </div>
        </div>

        <div ref={bottomRef}> </div>
      </form>
    </section>
  )
}

export default LoanPurposeForm
